import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createApiCall } from 'services'

type UnlinkCertificateGroupParams = {
  certificateId: string
  /** Additional certificate ids to unlink together (e.g. splitting a whole group). */
  childIds?: string[]
}

type UseCertificateGroupUnlinkOptions = {
  onSuccess?: (data?: unknown) => void
  onError?: (error?: unknown) => void
}

// VRG-235 — "Split group": unlinks the certificate (and, for a full split,
// all of its children) from the group. Rejected server-side for indivisible
// groups — the UI never offers the entry point there in the first place.
export const useCertificateGroupUnlink = (options?: UseCertificateGroupUnlinkOptions) => {
  const queryClient = useQueryClient()

  const unlinkMutation = useMutation({
    mutationFn: async ({ certificateId, childIds }: UnlinkCertificateGroupParams) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/groups/${certificateId}/unlink`,
        data: childIds?.length ? { childIds } : undefined,
      })
      return response.data
    },
    onSuccess: (data, variables) => {
      const affectedIds = [variables.certificateId, ...(variables.childIds ?? [])]
      affectedIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['certificates', 'detail', id] })
        queryClient.invalidateQueries({ queryKey: ['certificates', 'group', id] })
      })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    unlinkCertificateGroup: (params: UnlinkCertificateGroupParams) => unlinkMutation.mutate(params),
    isUnlinking: unlinkMutation.isPending,
  }
}
